import { Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';

import {
  ELinkOutcome,
  ILinkOutcomeResolution,
  INormalizedErrorBody,
  INormalizedHttpError,
  LINK_OUTCOME_ROUTES,
} from '@literax/models/link-outcome.model';

/**
 * Reason names the backend returns on a 401, from the guest authentication handler.
 */
const REASON_TOKEN_EXPIRED = 'tokenexpired';
const REASON_TOKEN_MALFORMED = 'tokenmalformed';

/**
 * Outcome names the backend returns on a 400, from the document service.
 */
const CODE_DOCUMENT_EXPIRED = 'documentexpired';
const CODE_DOCUMENT_UNAVAILABLE = 'documentunavailable';
const CODE_GUEST_ALREADY_PARTICIPATED = 'guestalreadyparticipated';
const CODE_ALREADY_SIGNED_OR_NOT_FOUND = 'documentsalreadysignedornotfound';

/**
 * Turns a failed guest-link request into exactly one outcome and the screen that explains it.
 *
 * This is the single place the SPA decides why a guest link cannot be opened. Nothing else may
 * branch on an HTTP error to pick a screen (FR-001, FR-004, D-03).
 *
 * Two rules matter more than the table itself:
 *
 * - The decision is made from `codeName`, never from the integer `code`. The integers exist for
 *   machines that cannot read strings, and the two families of code — authentication reasons and
 *   document outcomes — are numbered independently (D-06, FR-026).
 * - Expiry is never inferred from the token's own contents. An expired token is rejected by the
 *   backend and never reaches the document service, which is also why the FR-014 precedence
 *   (expired token beats unavailable document) needs no special handling here: the two arrive as
 *   different HTTP statuses and cannot both be true of one response (D-02).
 */
@Injectable({
  providedIn: 'root',
})
export class LinkOutcomeService {
  constructor(private router: Router) {}

  /**
   * Resolves a normalized HTTP error to its outcome and destination.
   *
   * Never throws and never returns `null`: an unrecognized error resolves to the generic screen
   * rather than leaving the caller without a destination, which is how a blank page happened
   * (FR-002, FR-003).
   *
   * @param error The error as `BaseService.handleError` produced it.
   * @returns The outcome and the `UrlTree` that shows it.
   */
  public resolve(error: unknown): ILinkOutcomeResolution {
    return this.toResolution(this.outcomeFor(error));
  }

  /**
   * Resolves the outcome for a token that could not be read at all.
   *
   * Decided before any HTTP call is made, so a cut link never reaches the network (FR-027).
   *
   * @returns The truncated outcome and its destination.
   */
  public resolveTruncated(): ILinkOutcomeResolution {
    return this.toResolution(ELinkOutcome.TRUNCATED);
  }

  /**
   * Builds the destination for an outcome.
   *
   * @param outcome The outcome to route.
   * @returns The outcome paired with its `UrlTree`, or `null` for the usable case.
   */
  private toResolution(outcome: ELinkOutcome): ILinkOutcomeResolution {
    return {
      outcome,
      urlTree: this.urlTreeFor(outcome),
    };
  }

  /**
   * @param outcome The outcome to route.
   * @returns The `UrlTree` for the outcome, or `null` when no redirection is needed.
   */
  private urlTreeFor(outcome: ELinkOutcome): UrlTree | null {
    const route = LINK_OUTCOME_ROUTES[outcome];

    return route ? this.router.parseUrl(route) : null;
  }

  /**
   * Picks the outcome for an error, from its HTTP status and the reason name in its body.
   *
   * @param error The error as the HTTP layer produced it.
   * @returns Exactly one outcome.
   */
  private outcomeFor(error: unknown): ELinkOutcome {
    const normalized = this.asNormalizedError(error);

    if (!normalized) {
      return ELinkOutcome.UNKNOWN;
    }

    if (normalized.httpStatus === 401) {
      return this.outcomeForRejection(this.reasonNameFor(normalized));
    }

    return this.outcomeForDocument(this.codeNameFor(normalized));
  }

  /**
   * Maps a guest authentication rejection to its outcome.
   *
   * Only two reasons have a screen of their own. `TokenInvalid` and `TokenMissing` mean the token
   * was unusable in a way the recipient can do nothing about, so they land on the generic screen
   * rather than claiming an expiry that may not have happened.
   *
   * @param reason The `codeName` from the 401 body, lower-cased; empty when absent.
   * @returns The matching outcome.
   */
  private outcomeForRejection(reason: string): ELinkOutcome {
    switch (reason) {
      case REASON_TOKEN_EXPIRED:
        return ELinkOutcome.EXPIRED_TOKEN;
      case REASON_TOKEN_MALFORMED:
        return ELinkOutcome.TRUNCATED;
      default:
        return ELinkOutcome.UNKNOWN;
    }
  }

  /**
   * Maps a document outcome to its screen.
   *
   * @param codeName The `codeName` from the body, lower-cased; empty when absent.
   * @returns The matching outcome.
   */
  private outcomeForDocument(codeName: string): ELinkOutcome {
    switch (codeName) {
      case CODE_DOCUMENT_EXPIRED:
        return ELinkOutcome.EXPIRED_DOCUMENT;
      case CODE_DOCUMENT_UNAVAILABLE:
        return ELinkOutcome.UNAVAILABLE;
      case CODE_GUEST_ALREADY_PARTICIPATED:
        return ELinkOutcome.COMPLETED;
      case CODE_ALREADY_SIGNED_OR_NOT_FOUND:
        // Deliberately generic, not completed. The name covers "already signed" *and* "not
        // found", and the two cannot be told apart from the code alone — so treating it as
        // completed would show "you already signed this" to someone whose document simply does
        // not exist. It is never emitted on the guest link path (its only producer is
        // Crypto.Api's batch signature endpoint), so this costs nothing today and keeps the
        // wrong screen unreachable if that ever changes.
        return ELinkOutcome.UNKNOWN;
      default:
        return ELinkOutcome.UNKNOWN;
    }
  }

  /**
   * Reads the reason name from a 401, where the body sits nested under `message`.
   *
   * @param error The normalized error.
   * @returns The reason name in lower case, or an empty string when there is none.
   */
  private reasonNameFor(error: INormalizedHttpError): string {
    const body = error.message;

    if (!body || typeof body === 'string') {
      return '';
    }

    return this.normalizeName((body as INormalizedErrorBody).codeName);
  }

  /**
   * Reads the outcome name from the 400 family, where the body was flattened and lower-cased.
   *
   * The nested shape is accepted as a fallback so a future change to the normalizer cannot
   * silently send every document outcome to the generic screen.
   *
   * @param error The normalized error.
   * @returns The outcome name in lower case, or an empty string when there is none.
   */
  private codeNameFor(error: INormalizedHttpError): string {
    const flat = this.normalizeName(error.codename);

    return flat || this.reasonNameFor(error);
  }

  /**
   * @param value The raw name, in whatever casing it arrived.
   * @returns The name in lower case, or an empty string when it is not usable.
   */
  private normalizeName(value: unknown): string {
    return typeof value === 'string' ? value.trim().toLowerCase() : '';
  }

  /**
   * @param error The value thrown by the HTTP layer.
   * @returns The error as a normalized object, or `null` when it is not one.
   */
  private asNormalizedError(error: unknown): INormalizedHttpError | null {
    return typeof error === 'object' && error !== null
      ? (error as INormalizedHttpError)
      : null;
  }
}
